// A reference SlideDeck exercising every one of the 18 kinds. Used by the
// renderer tests, the eval render-fit harness, and manual verification against
// the prototype. Mirrors the prototype data.jsx field shapes.
import type { SlideDeck } from './slide-model';

export const sampleDeck: SlideDeck = {
  schemaVersion: 2,
  meta: {
    title: 'Workplace by Ricoh — Q3 Board Review',
    subtitle: 'Platform momentum, adoption and the road to GA',
    author: 'RDx',
    date: 'Q3 2026',
    theme: 'workplace-paper',
    brand: '#1E2A3A',
  },
  slides: [
    {
      id: 's-cover', kind: 'cover', label: '01 · Cover', coverStyle: 'classic', art: 'slate',
      eyebrow: 'Board review', title: 'Workplace by Ricoh\nis live and growing',
      sub: 'One platform for the connected workplace', foot: 'Q3 2026', byline: 'RDx · Strategy & Commercial',
    },
    {
      id: 's-agenda', kind: 'agenda', label: '02 · Agenda', eyebrow: 'Today', title: 'What we will cover',
      items: [
        { t: 'Where we are', n: 'Adoption and revenue' },
        { t: 'What changed', n: 'Manage, Employee App, AI', active: true },
        { t: 'What is next', n: 'GA and the rollout plan' },
      ],
    },
    {
      id: 's-stat', kind: 'stat-row', label: '03 · Traction', eyebrow: 'At a glance', title: 'The platform at scale',
      lede: 'Live across the estate today.',
      stats: [
        { n: '203', label: 'Customers' },
        { n: '46,178', accent: '46,178', label: 'Spaces mapped' },
        { n: '23M', label: 'Bookings' },
      ],
    },
    {
      id: 's-bignum', kind: 'bignum', label: '04 · Revenue', eyebrow: 'Recurring', title: 'Monthly recurring revenue',
      lede: 'Up and to the right.', value: '€74K', vlabel: 'MRR, EU region', delta: '+18% QoQ',
    },
    {
      id: 's-features', kind: 'features', label: '05 · Pillars', eyebrow: 'The platform', title: 'Three product pillars',
      cards: [
        { icon: 'home', h: 'Spaces', d: 'Desk and room booking, indoor mapping, occupancy.' },
        { icon: 'trending', h: 'Manage', d: 'Operational intelligence and AI-contextualised issues.' },
        { icon: 'shield', h: 'Identity', d: 'One sign-in across every workplace surface.' },
      ],
    },
    {
      id: 's-chart', kind: 'chart', label: '06 · Adoption', eyebrow: 'By product', title: 'Active usage by module',
      lede: 'Manage is the fastest riser.', unit: '%', source: 'Platform analytics, May 2026',
      bars: [
        { k: 'Spaces', v: 82, note: 'Spaces leads at <b>82%</b> weekly active.' },
        { k: 'Manage', v: 64, note: 'Manage at <b>64%</b> and climbing.' },
        { k: 'Employee App', v: 47, note: 'Employee App at <b>47%</b>, newly launched.' },
        { k: 'Visitor', v: 38, note: 'Visitor management at <b>38%</b>.' },
      ],
    },
    {
      id: 's-trend', kind: 'trend', label: '07 · Growth', eyebrow: 'Trailing 12m', title: 'Bookings per month',
      lede: 'Steady compounding growth.', prefix: '', unit: 'M', source: 'vw_fct_bookings',
      points: [
        { x: 'Jun', v: 1.4 }, { x: 'Aug', v: 1.6 }, { x: 'Oct', v: 1.7, note: 'Manage GA' },
        { x: 'Dec', v: 1.9 }, { x: 'Feb', v: 2.0 }, { x: 'Apr', v: 2.2, note: 'Record month' },
      ],
    },
    {
      id: 's-kpi', kind: 'kpi', label: '08 · Health', eyebrow: 'This quarter', title: 'Operating KPIs',
      kpis: [
        { label: 'NRR', value: '112%', delta: '4pts', up: true, spark: [98, 101, 104, 106, 109, 112] },
        { label: 'Churn', value: '1.8%', delta: '0.3pts', up: true, spark: [3, 2.6, 2.3, 2.1, 1.9, 1.8] },
        { label: 'CSAT', value: '4.6', delta: '0.1', up: true, spark: [4.3, 4.3, 4.4, 4.5, 4.5, 4.6] },
        { label: 'Tickets', value: '312', delta: '40', up: false, spark: [410, 390, 360, 340, 330, 312] },
      ],
    },
    {
      id: 's-bullets', kind: 'bullets', label: '09 · Wins', eyebrow: 'Highlights', title: 'What shipped this quarter',
      lede: 'A heavy delivery quarter.',
      items: [
        'Manage V1 in market across the Barcelona estate',
        'Employee App home redesign with Spotlight and QuickBook',
        'Workplace AI booking assistant in private beta',
        'Visitor Management standalone launched',
      ],
    },
    {
      id: 's-split', kind: 'split', label: '10 · Spotlight', side: 'left', art: 'sage',
      eyebrow: 'Customer', title: 'RESCM goes platform-wide', caption: 'Barcelona HQ',
      lede: 'From a single pilot to the whole estate in two quarters.',
      items: ['560 locations onboarded', 'Single identity across apps', '€2.0M corrected TCV'],
    },
    {
      id: 's-gallery', kind: 'gallery', label: '11 · Surfaces', eyebrow: 'One platform', title: 'Every workplace surface',
      items: [
        { art: 'dawn', t: 'Mobile', n: 'Book on the go' },
        { art: 'mist', t: 'Web', n: 'Plan your week' },
        { art: 'dusk', t: 'Room panels', n: 'Tap to book' },
        { art: 'ember', t: 'Kiosk', n: 'Sign in on arrival' },
      ],
    },
    {
      id: 's-pricing', kind: 'pricing', label: '12 · Packaging', eyebrow: 'Commercial', title: 'Three ways to buy',
      lede: 'Observe, Operate, Optimise.',
      tiers: [
        { name: 'Observe', price: '£8', period: '/desk/mo', note: 'Analytics only', features: ['Occupancy', 'Dashboards', 'Exports'] },
        { name: 'Operate', price: '£14', period: '/desk/mo', featured: true, tag: 'Most popular', note: 'The core bundle', features: ['Booking', 'Mapping', 'Manage', 'Support'] },
        { name: 'Optimise', price: '£22', period: '/desk/mo', note: 'AI + integrations', features: ['Everything in Operate', 'Workplace AI', 'Integrations', 'Priority SLA'] },
      ],
    },
    {
      id: 's-timeline', kind: 'timeline', label: '13 · Roadmap', eyebrow: 'The plan', title: 'Road to GA',
      milestones: [
        { date: 'Q1', t: 'Foundation', n: 'Platform readiness', done: true },
        { date: 'Q2', t: 'Manage GA', n: 'Operational intelligence', done: true },
        { date: 'Q3', t: 'AI beta', n: 'Booking assistant', active: true },
        { date: 'Q4', t: 'Platform GA', n: 'Full launch' },
      ],
    },
    {
      id: 's-compare', kind: 'compare', label: '14 · Why us', eyebrow: 'Versus point tools', title: 'One platform beats five tools',
      left: { tag: 'Point tools', title: 'Stitched together', items: ['Five logins', 'No shared data', 'Manual ops', 'No AI'] },
      right: { tag: 'Workplace by Ricoh', title: 'One platform', highlight: true, items: ['Single sign-in', 'Shared data model', 'AI-contextualised ops', 'One contract'] },
    },
    {
      id: 's-quote', kind: 'quote', label: '15 · Voice', eyebrow: 'Customer voice', title: 'What customers say',
      quote: 'It is the first time our buildings feel like one system rather than a pile of apps.',
      name: 'Head of Workplace', meta: 'Global professional services firm', avatar: 'HW',
    },
    {
      id: 's-quoteimg', kind: 'quote-image', label: '16 · Voice', art: 'slate', animate: true,
      quote: 'Adoption was immediate — people just started using it.',
      name: 'Facilities Director', meta: 'RESCM', avatar: 'FD',
    },
    {
      id: 's-fullbleed', kind: 'full-bleed', label: '17 · Vision', art: 'dusk', animate: true, align: 'center',
      eyebrow: 'Where we are going', title: 'The connected workplace, by Ricoh',
      lede: 'Every space, every surface, one intelligent platform.',
    },
    // ── Image-bearing layout variants ──────────────────────────────────────
    // These exercise EVERY image-capable layout so the imageSlotsOf↔render
    // parity sweep (render-slides.test.ts) actually covers the slots each
    // variant emits. Appended AFTER the canonical first-of-kind slides above so
    // the many `slides.find(s => s.kind === k)` tests keep resolving the
    // original variants. Each carries a distinct id + a "Nb" label.
    {
      id: 's-cover-image', kind: 'cover', label: '01b · Cover (image)', layout: 'with-image',
      eyebrow: 'Board review', title: 'One platform,\none connected workplace',
      sub: 'The story in a single frame', byline: 'RDx',
      background: { type: 'image', img: 'generate:a bright modern office reception with a wall-mounted booking tablet', scrim: 'strong' },
    },
    {
      id: 's-cover-panel', kind: 'cover', label: '01c · Cover (side panel)', layout: 'side-panel',
      eyebrow: 'Board review', title: 'Momentum you can see',
      sub: 'Title left, media right', byline: 'RDx',
      background: { type: 'image', img: 'generate:a colleague tapping a meeting-room door panel to check in' },
    },
    {
      id: 's-cover-portrait', kind: 'cover', label: '01d · Cover (split portrait)', layout: 'split-portrait',
      eyebrow: 'Board review', title: 'The connected\nworkplace, by Ricoh',
      sub: 'Title left, tall media panel right', byline: 'RDx',
      background: { type: 'image', img: 'generate:a staff member checking a desk-booking screen in a daylit office' },
    },
    {
      id: 's-agenda-image', kind: 'agenda', label: '02b · Agenda (with image)', layout: 'with-image',
      eyebrow: 'Today', title: 'What we will cover', panelArt: 'sage',
      items: [
        { t: 'Where we are', n: 'Adoption and revenue' },
        { t: 'What changed', n: 'Manage, Employee App, AI' },
        { t: 'What is next', n: 'GA and the rollout plan' },
      ],
    },
    {
      id: 's-bullets-image', kind: 'bullets', label: '09b · Wins (with image)', layout: 'with-image',
      eyebrow: 'Highlights', title: 'A heavy delivery quarter', panelArt: 'ember',
      lede: 'The headlines at a glance.',
      items: [
        'Manage V1 in market across the Barcelona estate',
        'Employee App home redesign shipped',
        'Workplace AI booking assistant in private beta',
      ],
    },
    {
      id: 's-kpi-image', kind: 'kpi', label: '08b · Health (with image)', layout: 'with-image',
      eyebrow: 'This quarter', title: 'Operating KPIs', panelArt: 'ocean',
      kpis: [
        { label: 'NRR', value: '112%', delta: '4pts', up: true, spark: [98, 104, 109, 112] },
        { label: 'CSAT', value: '4.6', delta: '0.1', up: true, spark: [4.3, 4.4, 4.5, 4.6] },
      ],
    },
    {
      id: 's-cta', kind: 'cta', label: '18 · Close', eyebrow: 'Thank you',
      headline: 'Let us take it to GA', sub: 'Questions, then the rollout plan.',
      contact: [['Owner', 'Nathan Thomas'], ['Team', 'RDx'], ['Next', 'Q4 GA']],
    },
  ],
  notes: {
    's-cover': 'Open with momentum — we are live, growing, and ahead of plan.',
    's-bignum': 'MRR is EU-only; AU adds on top. 18% QoQ is our best quarter.',
    's-chart': 'Hover to read each bar. Manage is the story this quarter.',
  },
};
